import {
    AspectRatio,
    Box,
    Flex,
    Skeleton,
    Stack,
    useMediaQuery
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'

import ScrollerHeader from './scroller-header'
import {useScroller} from './use-scroller'
import MobileScroller from './mobile'

const Scroller = ({data}) => {
    const items = data?.items ?? []
    const containerId = `ProductCarousel-${data?.sys?.id}`
    const [isDesktop] = useMediaQuery('(min-width: 1024px)')
    const showNavArrows = isDesktop && items?.length > 6

    const {scroll, scrollRef} = useScroller({
        containerId,
        items,
        itemWidth: 'auto',
        defaultNumberOfItems: 6
    })

    if (!items?.length)
        return (
            <Flex flexDirection="column" gap="20px">
                <Skeleton width={{base: '80%', lg: '30%'}} height="40px" />
                <Flex
                    width={'100%'}
                    display={{base: 'flex', lg: 'none'}}
                    padding="2px"
                    justifyContent="space-evenly"
                    alignItems="flex-start"
                >
                    {[0, 1].map((key) => (
                        <Skeleton key={key} width="40%" height="200px" />
                    ))}
                </Flex>

                <Flex
                    display={{base: 'none', lg: 'flex'}}
                    gap="12px"
                    flexDirection={{base: 'column', lg: 'row'}}
                >
                    {[0, 1, 2, 3].map((key) => (
                        <React.Fragment key={key}>
                            <Box
                                key={key}
                                flex="0 0 auto"
                                width="100%"
                                style={{scrollSnapAlign: 'start'}}
                            >
                                <Stack data-testid="product-scroller-item-skeleton">
                                    <AspectRatio ratio={1}>
                                        <Skeleton />
                                    </AspectRatio>
                                    <Stack spacing={2}>
                                        <Skeleton width="150px" height={5} />
                                        <Skeleton width="75px" height={5} />
                                    </Stack>
                                </Stack>
                            </Box>
                        </React.Fragment>
                    ))}
                </Flex>
            </Flex>
        )

    if (!isDesktop) return <MobileScroller data={data} />

    return (
        <Flex
            flexFlow="column"
            position="relative"
            alignItems="center"
            justifyContent="center"
            width={{base: '100%', md: '80%', xl: '80%'}}
        >
            <ScrollerHeader
                title={data?.heading}
                scroll={scroll}
                viewAll={data?.cta?.fields}
                showScrollerNav={showNavArrows}
            />
            <Stack
                width="100%"
                minHeight="284px"
                position="relative"
                maxWidth={{
                    base: 'calc(100vw - 40px)',
                    lg: 'calc(100vw - 32px)',
                    xl: '100%'
                }}
            >
                <Stack
                    ref={scrollRef}
                    gap="24px"
                    width="100%"
                    direction="row"
                    wrap={{base: 'unset', xl: 'nowrap'}}
                    transition="transform 0.4s ease-in-out"
                    overflowX={{base: 'auto', xl: 'scroll'}}
                    justifyContent="center"
                    sx={{
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        overflowX: 'auto',
                        '-ms-overflow-style': 'none',
                        'scrollbar-width': 'none',
                        scrollBehavior: 'smooth',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        }
                    }}
                    id={containerId}
                >
                    {items.map((item, ind) => (
                        <Stack
                            key={ind}
                            style={{
                                'scroll-snap-align': 'start'
                            }}
                        >
                            {item}
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Flex>
    )
}

Scroller.displayName = 'ProductCarousel'

Scroller.propTypes = {
    data: PropTypes.shape({
        heading: PropTypes.string,
        cta: PropTypes.object,
        sys: PropTypes.shape({
            id: PropTypes.string
        }),
        items: PropTypes.arrayOf(PropTypes.obj)
    })
}

export default Scroller
